import apiService from './api';
import { RecipeData, ChatMessage } from '../types';
import { RecipeCreateRequest } from '../types/api.types';
import { create } from 'lodash';

class RecipeService {
  private readonly STORAGE_KEY = 'recipe_history';

  async createRecipe(
    tableId: string, 
    name: string, 
    chatHistory: ChatMessage[]
  ): Promise<RecipeData> {
    try {
      const requestData: RecipeCreateRequest = {
        tableId,
        name,
        description: this._generateDescription(chatHistory),
        chatHistory,
        userId: apiService.getUserId(),
      };

      // 如果API不可用，使用本地存储
      try {
        const response = await apiService.createRecipe<RecipeData>(requestData);
        return response.data;
      } catch (apiError) {
        console.warn('API创建配方失败，使用本地存储:', apiError);
        return this._createLocalRecipe(requestData);
      }
    } catch (error) {
      console.error('创建配方失败:', error);
      throw error;
    }
  }

  async getRecipeHistory(): Promise<RecipeData[]> {
    try {
      // 优先尝试从API获取
      try {
        const response = await apiService.getRecipeHistory(apiService.getUserId());
        if (Array.isArray(response.data)) {
          return response.data.map((recipe: any) => ({
            ...recipe,
            tableId: recipe.table_name,
            name: recipe.table_title,
            createdAt: recipe.date_created,
          }));
        } else {
          console.warn('API返回的数据格式不正确，期望数组类型:', response.data);
          return [];
        }
      } catch (apiError) {
        console.warn('API获取历史失败，使用本地存储:', apiError);
        return [];
      }
    } catch (error) {
      console.error('获取配方历史失败:', error);
      return [];
    }
  }

  async getRecipeChatHistory(): Promise<ChatMessage[]> {
    try {
      // 优先尝试从API获取
      try {
        const response = await apiService.getRecipeChatHistory(apiService.getUserId());
        if (Array.isArray(response.data)) {
          return response.data.map((chat: any) => ({
            ...chat,
            tableId: chat.intelligent_table,
          }));
        } else {
          console.warn('API返回的数据格式不正确，期望数组类型:', response.data);
          return [];
        }
      } catch (apiError) {
        console.warn('API获取历史失败，使用本地存储:', apiError);
        // return this._getLocalHistory();
        return [];
      }
    } catch (error) {
      console.error('获取配方历史失败:', error);
      return [];
    }
  }

  async getTableCollectionName(recipeId: string): Promise<RecipeData[]> {
    try {
      // 优先尝试从API获取
      try {
        const response = await apiService.getTableCollectionName(recipeId);
        if (Array.isArray(response.data)) {
          return response.data.map((recipe: any) => ({
            ...recipe,
            tableId: recipe.table_name,
            name: recipe.table_title,
            createdAt: recipe.date_created,
          }));
        } else {
          console.warn('API返回的数据格式不正确，期望数组类型:', response.data);
          return [];
        }
      } catch (apiError) {
        console.warn('API获取历史失败，使用本地存储:', apiError);
        return [];
      }
    } catch (error) {
      console.error('获取配方历史失败:', error);
      return [];
    }
  }

  async deleteRecipe(recipeId: string): Promise<void> {
    try {
      // 尝试从API删除
      try {
        await apiService.deleteRecipe(recipeId);
      } catch (apiError) {
        console.warn('API删除失败，从本地删除:', apiError);
      }

      // 从本地存储删除
      this._deleteLocalRecipe(recipeId);
    } catch (error) {
      console.error('删除配方失败:', error);
      throw error;
    }
  }

  private _generateDescription(chatHistory: ChatMessage[]): string {
    const userMessages = chatHistory.filter(msg => msg.type === 'user');
    if (userMessages.length > 0) {
      return userMessages[0].content.substring(0, 100) + '...';
    }
    return '自动生成的配方';
  }

  private _createLocalRecipe(requestData: RecipeCreateRequest): RecipeData {
    const recipe: RecipeData = {
      id: `recipe_${Date.now()}`,
      name: requestData.name,
      tableId: requestData.tableId,
      description: requestData.description,
      ingredients: [],
      properties: [],
      chatHistory: requestData.chatHistory,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const history = this._getLocalHistory();
    history.unshift(recipe);
    this._saveLocalHistory(history);

    return recipe;
  }

  private _getLocalHistory(): RecipeData[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('读取本地历史失败:', error);
      return [];
    }
  }

  private _saveLocalHistory(history: RecipeData[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('保存本地历史失败:', error);
    }
  }

  private _deleteLocalRecipe(recipeId: string): void {
    const history = this._getLocalHistory();
    const filtered = history.filter(recipe => recipe.id !== recipeId);
    this._saveLocalHistory(filtered);
  }
}

export default new RecipeService();